import cn from 'classnames';
import React, { ComponentType, MouseEventHandler, PropsWithChildren } from 'react';
import { BallFall } from './animations';

export type DSButtonSize = 'small' | 'medium';
export type DSButtonColor = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'error' | 'bet' | 'bet-selected';

export interface DSButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
	size: DSButtonSize;
	color: DSButtonColor;
	type?: 'submit' | 'button';
	disabled?: boolean;
	leftIcon?: ComponentType<any>;
	rightIcon?: ComponentType<any>;
	onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
	busy?: boolean;
	cyTestRef?: string;
}

export const DSButton = (props: PropsWithChildren<DSButtonProps>) => {
	const colorClasses = () => {
		switch (props.color) {
			case 'primary':
			case 'bet': // Bet button is the same as primary but taller
				return 'bg-accent-400  hover:bg-accent-500  active:bg-accent-600  focus:bg-accent-500  disabled:bg-accent-100 text-primary-400 disabled:text-gray-250';
			case 'secondary':
				return 'bg-primary-400 hover:bg-primary-500 active:bg-primary-600 focus:bg-primary-500 disabled:bg-gray-250   text-gray-100    disabled:text-white';
			case 'error':
				return 'bg-error-600   hover:bg-error-650   active:bg-error-700   focus:bg-error-650   disabled:bg-error-200  text-gray-100    disabled:text-gray-250';
			case 'tertiary':
				return cn(
					'     bg-primary-100      hover:bg-primary-200      active:bg-primary-300      focus:bg-primary-100      disabled:bg-gray-200                               text-primary-400 disabled:text-primary-100',
					'dark:bg-dark-400    dark:hover:bg-dark-500    dark:active:bg-dark-300    dark:focus:bg-dark-400    dark:disabled:bg-dark-500 dark:disabled:opacity-30 dark:text-gray-100',
				);
			case 'bet-selected':
				return 'bg-primary-600 hover:bg-primary-400 focus:bg-primary-400 disabled:bg-gray-250   text-gray-100    disabled:text-white';
			case 'ghost':
				return cn(
					// Both `disabled:bg-transparent` and `dark:disabled:bg-transparent` are needed to prevent changing background color on hover when button is disabled
					'bg-transparent focus:bg-transparent disabled:bg-transparent dark:disabled:bg-transparent disabled:opacity-30',
					'     hover:bg-primary-100      active:bg-primary-200      text-primary-400      disabled:text-primary-500',
					'dark:hover:bg-dark-300    dark:active:bg-dark-400    dark:text-gray-100    dark:disabled:text-gray-100',
				);
		}
	};

	const classes = () =>
		cn(
			'relative',
			'inline-flex items-center justify-center gap-2',
			'focus:outline focus:outline-2 focus:outline-info-600',
			'font-semibold whitespace-nowrap',
			'rounded-4',
			'cursor-pointer',
			{
				'h-12': ['bet', 'bet-selected'].includes(props.color),
			},
			{
				'py-3 text-label-sm': props.size === 'small',
				'py-4 text-label-md': props.size === 'medium',
			},
			props.leftIcon
				? {
						'pl-3': props.size === 'small',
						'pl-8': props.size === 'medium',
				  }
				: {
						'pl-4': props.size === 'small',
						'pl-10': props.size === 'medium',
				  },
			props.rightIcon
				? {
						'pr-3': props.size === 'small',
						'pr-8': props.size === 'medium',
				  }
				: {
						'pr-4': props.size === 'small',
						'pr-10': props.size === 'medium',
				  },
			colorClasses(),
			props.className,
		);

	const iconContainerClasses = () =>
		cn(
			'icon-thick',
			{
				'w-3.5 h-3.5': props.size === 'small',
				'w-5 h-5': props.size === 'medium',
			},
			{
				invisible: props.busy,
			},
		);

	return (
		<button
			type={props.type || 'button'}
			disabled={props.disabled || props.busy}
			className={classes()}
			onClick={props.onClick}
			data-cy={props.cyTestRef}
		>
			{props.busy && (
				<div className="absolute w-full h-full overflow-hidden flex items-center justify-center">
					<BallFall />
				</div>
			)}
			{props.leftIcon && (
				<div className={iconContainerClasses()}>
					<props.leftIcon />
				</div>
			)}
			<div className={props.busy ? 'invisible' : ''}>{props.children}</div>
			{props.rightIcon && (
				<div className={iconContainerClasses()}>
					<props.rightIcon />
				</div>
			)}
		</button>
	);
};
